/*
 * Copyright (c) 2025, FusionAuth, All Rights Reserved
 */

interface ElTabGroup extends HTMLElement {
  setActiveTab(index: number): void;
}

// Declare prime as global since it's loaded via script tag
declare global {
  namespace prime {
    class Tabs {
      constructor();
    }
  }
}

/**
 * Service to manage tab state persistence using session storage.
 * @extends Tabs
 */
export class TabsService {
  #storageKeys: { [tabsId: string]: string } = {};

  constructor() {
    Prime.Document.onReady(() => {
      document.querySelectorAll<ElTabGroup>('el-tab-group').forEach((tabs) => {
        const tabsId = tabs.id;
        if (!tabsId) {
          return;
        }

        // Generate storage key based on URL and tab group id
        const key = this.#getStorageKey(tabsId);

        // Retrieve selected tab index from session storage
        const storedIndex = sessionStorage.getItem(key);
        const foundIndex = storedIndex !== null ? parseInt(storedIndex, 10) : 0;

        const buttonsList = tabs.querySelector<HTMLElement>('.tab-list-container');
        const buttons = [...buttonsList?.children || []] as HTMLElement[];

        buttons.forEach((button, index) => {
          button.addEventListener('click', _ => {
            sessionStorage.setItem(key, index.toString());
          });
        });

        const errorTabs = this.#checkForErrors(buttons);
        if (errorTabs.length > 0) {
          const errorTabIndex = buttons.indexOf(errorTabs[0]);
          tabs.setActiveTab(errorTabIndex);
          return;
        }

        if (!isNaN(foundIndex)) {
          tabs.setActiveTab(foundIndex);
        }
      });
    }, 0);
  }

  // Generates a session storage key based on the URL and tab group id
  #getStorageKey(tabsId: string): string {
    let key = this.#storageKeys[tabsId];
    if (key) {
      return key;
    }
    const url = new URL(window.location.href);
    const pathSegments = url.pathname.split('/').filter(Boolean);

    // Find the segments after 'admin'
    const adminIndex = pathSegments.indexOf('admin');
    const relevantSegments = adminIndex >= 0 ? pathSegments.slice(adminIndex + 1) : pathSegments;

    key = this.#storageKeys[tabsId] = `${relevantSegments.join('.')}.${tabsId}.tabs`;
    return key;
  }

  #checkForErrors(buttons: HTMLElement[]) {
    return buttons.filter((button) => {
      const tabId = button.getAttribute('aria-controls');
      if (tabId) {
        const tab = document.getElementById(tabId);
        if (tab) {
          const errorElements = tab.querySelectorAll('span.error');
          const hasError = errorElements.length > 0;
          if (hasError) {
            button?.classList.remove('text-slate-500');
            button?.classList.add('text-rose-500');
            return true;
          }
        }
      }
      return false;
    });
  }
}

export default new TabsService();
